/**
 * Demo Center launcher service (WQ-337).
 *
 * Opens a Demo Center session (POST /api/demos/ticket → /demos/_auth, WQ-336) and
 * reads /demos/manifest.json. The manifest is untrusted input (scoping packet §5):
 * every field is validated here and rendered as plain text by the view.
 *
 * Both CloudFront distributions rewrite origin 403/404 into the platform's
 * index.html with status 200, and with the demos container down /demos/* falls
 * through to the SPA (200 text/html). So no response is trusted unless it is
 * JSON (or, for /demos/_auth, exactly 204).
 */

export type DemoKind = 'application' | 'dashboard';
export type DemoOpenMode = 'new-tab' | 'embed';

export interface DemoEntry {
  slug: string;
  title: string;
  description: string;
  path: string;
  kind: DemoKind;
  open: DemoOpenMode;
}

export type DemoSessionResult =
  | { status: 'ok' | 'not-entitled' | 'session-error' | 'unavailable' }
  | { status: 'wrong-host'; origin: string };

export type ManifestResult =
  | { status: 'ok'; raw: unknown }
  | { status: 'unavailable' }
  | { status: 'session-error' }
  | { status: 'invalid-manifest' };

export interface ValidatedManifest {
  valid: boolean;
  demos: DemoEntry[];
}

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

export const MAX_MANIFEST_ENTRIES = 50;
const SLUG_RE = /^[a-z0-9-]{1,64}$/;
const PATH_RE = /^\/demos\/[A-Za-z0-9._~/-]*$/;
const KINDS: readonly string[] = ['application', 'dashboard'];
const OPEN_MODES: readonly string[] = ['new-tab', 'embed'];

export const KIND_LABELS: Record<DemoKind, string> = {
  application: 'Application',
  dashboard: 'Dashboard',
};

function contentType(response: Response): string {
  return (response.headers?.get('content-type') ?? '').toLowerCase();
}

function isJson(response: Response): boolean {
  return contentType(response).includes('application/json');
}

function isHtml(response: Response): boolean {
  return contentType(response).includes('text/html');
}

/** The ticket's `origin` as a normalised http(s) origin, or null if absent or malformed. */
function demoOrigin(raw: unknown): string | null {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const url = new URL(raw);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null;
  } catch {
    return null;
  }
}

/**
 * Ticket → cookie. Resolves to a state the view can show; never throws.
 *
 * Only the tenant origin serves /demos (DC-1), so on any other host the ticket's
 * `origin` sends the user there instead of redeeming locally (WQ-346).
 *
 * The /demos/_auth router is declared by the demos container itself, so with that
 * container down the request falls through to the SPA (200 text/html). Redeem only
 * ever answers 204/302/401, so an HTML answer means "not deployed", not a bad session.
 */
export async function ensureDemoSession(authFetch: AuthFetch): Promise<DemoSessionResult> {
  let code: string;
  try {
    const ticket = await authFetch('/api/demos/ticket', { method: 'POST' });
    if (!ticket.ok || !isJson(ticket)) return { status: 'session-error' };
    const body = await ticket.json();
    if (body?.entitled === false) return { status: 'not-entitled' };
    if (body?.entitled !== true || typeof body.code !== 'string' || !body.code) return { status: 'session-error' };
    const origin = demoOrigin(body.origin);
    if (origin && origin !== window.location.origin) return { status: 'wrong-host', origin };
    code = body.code;
  } catch {
    return { status: 'session-error' };
  }
  try {
    const redeem = await fetch(`/demos/_auth?code=${encodeURIComponent(code)}&mode=json`, {
      credentials: 'same-origin',
    });
    if (redeem.status === 204) return { status: 'ok' };
    return { status: isHtml(redeem) ? 'unavailable' : 'session-error' };
  } catch {
    return { status: 'session-error' };
  }
}

/** Fetch the manifest; anything that is not a JSON 200 is reported, never parsed. */
export async function fetchManifest(): Promise<ManifestResult> {
  let response: Response;
  try {
    response = await fetch('/demos/manifest.json', { credentials: 'same-origin' });
  } catch {
    return { status: 'unavailable' };
  }
  if (!isJson(response)) return { status: 'unavailable' };
  if (!response.ok) return { status: 'session-error' };
  try {
    return { status: 'ok', raw: await response.json() };
  } catch {
    return { status: 'invalid-manifest' };
  }
}

function isSafePath(path: unknown): path is string {
  return typeof path === 'string' && PATH_RE.test(path) && !path.includes('..') && !path.includes('//');
}

function validateEntry(entry: unknown): DemoEntry | null {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return null;
  const e = entry as Record<string, unknown>;
  if (typeof e.slug !== 'string' || !SLUG_RE.test(e.slug)) return null;
  if (typeof e.title !== 'string' || !e.title.trim() || e.title.length > 120) return null;
  if (typeof e.description !== 'string' || e.description.length > 500) return null;
  if (!isSafePath(e.path)) return null;
  if (typeof e.kind !== 'string' || !KINDS.includes(e.kind)) return null;
  const open = e.open === undefined ? 'new-tab' : e.open;
  if (typeof open !== 'string' || !OPEN_MODES.includes(open)) return null;
  return {
    slug: e.slug,
    title: e.title,
    description: e.description,
    path: e.path,
    kind: e.kind as DemoKind,
    open: open as DemoOpenMode,
  };
}

/**
 * Validate an untrusted manifest. An invalid document yields no demos and
 * `valid: false`; invalid entries are dropped one by one. Unknown fields are ignored.
 */
export function validateManifest(raw: unknown): ValidatedManifest {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { valid: false, demos: [] };
  const doc = raw as Record<string, unknown>;
  if (doc.schema !== 1 || !Array.isArray(doc.demos)) return { valid: false, demos: [] };

  const demos: DemoEntry[] = [];
  const seen = new Set<string>();
  doc.demos.slice(0, MAX_MANIFEST_ENTRIES).forEach((entry, index) => {
    const valid = validateEntry(entry);
    const slug = (entry as { slug?: unknown } | null)?.slug;
    const name = typeof slug === 'string' ? `"${slug.slice(0, 64)}"` : `#${index}`;
    if (!valid) {
      console.warn(`[DemoCenter] manifest entry ${name} dropped: invalid`);
    } else if (seen.has(valid.slug)) {
      console.warn(`[DemoCenter] manifest entry ${name} dropped: duplicate slug`);
    } else {
      seen.add(valid.slug);
      demos.push(valid);
    }
  });
  return { valid: true, demos };
}
